import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
 Heart, Target, Eye, Info, AlertTriangle, TrendingUp, TrendingDown,
 Minus, ThumbsUp, AlertCircle, HelpCircle, ArrowLeft, X
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';

// Mock data
const mockEmployee = {
 id: 'emp_001',
 name: 'Sarah Johnson',
 position: 'Senior Product Designer',
 department: 'Design',
};

const mockBehaviors = [
 { id: 'ownership', name: 'Ownership & Accountability', pattern: 'strength', patternLabel: 'Strength', trend: 'stable' },
 { id: 'collaboration', name: 'Collaboration', pattern: 'mixed-signals', patternLabel: 'Mixed signals', trend: 'declining' },
 { id: 'communication', name: 'Communication & Clarity', pattern: 'watch-area', patternLabel: 'Watch area', trend: 'improving' },
 { id: 'judgment', name: 'Judgment & Decision-Making', pattern: 'strength', patternLabel: 'Strength', trend: 'improving' },
 { id: 'learning', name: 'Learning & Growth', pattern: 'insufficient-evidence', patternLabel: 'Insufficient evidence', trend: 'stable' },
 { id: 'reliability', name: 'Reliability & Follow-Through', pattern: 'strength', patternLabel: 'Strength', trend: 'stable' },
];

const mockRecentChanges = [
 { type: 'concern', summary: 'New cross-team collaboration concern surfaced', timeframe: 'This cycle' },
 { type: 'appreciation', summary: 'Repeated ownership appreciation this cycle', timeframe: 'Last 3 weeks' },
 { type: 'shift', summary: 'Judgment signals shifted during high-pressure project', timeframe: '2 weeks ago' },
 { type: 'pattern', summary: 'Communication clarity improving with structured check-ins', timeframe: 'This month' },
];

const getPatternColor = (pattern: string) => {
 switch (pattern) {
 case 'strength': return 'border-green-200 bg-green-50';
 case 'mixed-signals': return 'border-yellow-200 bg-yellow-50';
 case 'watch-area': return 'border-orange-200 bg-orange-50';
 case 'insufficient-evidence': return 'border-border bg-gray-50';
 default: return 'border-border bg-gray-50';
 }
};

const getPatternBadgeColor = (pattern: string) => {
 switch (pattern) {
 case 'strength': return 'bg-green-100 text-green-700';
 case 'mixed-signals': return 'bg-yellow-100 text-yellow-700';
 case 'watch-area': return 'bg-orange-100 text-orange-700';
 case 'insufficient-evidence': return 'bg-gray-100 text-gray-600';
 default: return 'bg-gray-100 text-gray-600';
 }
};

const getTrendIcon = (trend: string) => {
 switch (trend) {
 case 'improving': return <TrendingUp className="w-3.5 h-3.5 text-green-500" />;
 case 'declining': return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
 case 'stable': return <Minus className="w-3.5 h-3.5 text-gray-700" />;
 default: return <Minus className="w-3.5 h-3.5 text-gray-700" />;
 }
};

const getChangeIcon = (type: string) => {
 switch (type) {
 case 'concern': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
 case 'appreciation': return <ThumbsUp className="w-4 h-4 text-green-500" />;
 case 'shift': return <AlertCircle className="w-4 h-4 text-blue-500" />;
 case 'pattern': return <TrendingUp className="w-4 h-4 text-purple-500" />;
 default: return <Info className="w-4 h-4 text-gray-500" />;
 }
};

export default function BehaviorValues() {
 const { t } = useTranslation();
 const [selectedBehavior, setSelectedBehavior] = useState<string | null>(null);

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <ComingSoonBanner
 icon={<Heart className="w-6 h-6" />}
 title="Behavior & Values"
 description="Behavior & Values is currently in development. This is a preview of the upcoming feature."
 gradientFrom="from-purple-500"
 gradientTo="to-pink-500"
 />

 {/* Header */}
 <div className="bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-indigo-500/10 border border-border rounded-xl p-6 mb-8">
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center space-x-4">
 <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
 <Target className="w-7 h-7 text-white" />
 </div>
 <div>
 <h1 className="text-3xl font-bold text-foreground">{t('Behavior / Values Layer')}</h1>
 <p className="text-sm text-foreground mt-1">
 {t('Lightweight behavioral lens -- Scannable in under 90 seconds')}
 </p>
 </div>
 </div>
 <button
 className="flex items-center space-x-2 px-4 py-2 border border-border text-foreground rounded-lg"
 >
 <ArrowLeft className="w-4 h-4" />
 <span>{t('Back to Module')}</span>
 </button>
 </div>

 {/* Employee Card */}
 <div className="bg-card border border-border rounded-lg p-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-4">
 <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-medium">
 {mockEmployee.name.split(' ').map(n => n[0]).join('')}
 </div>
 <div>
 <h3 className="text-lg font-semibold text-foreground">{mockEmployee.name}</h3>
 <p className="text-sm text-foreground">
 {mockEmployee.position} -- {mockEmployee.department}
 </p>
 </div>
 </div>
 <div className="flex items-center space-x-2">
 <span className="text-xs text-foreground">{t('Last updated')}:</span>
 <span className="text-sm font-medium text-foreground">2 {t('days ago')}</span>
 </div>
 </div>
 </div>
 </div>

 {/* 1. Behavior Snapshot */}
 <div className="mb-8">
 <div className="flex items-center space-x-2 mb-4">
 <Eye className="w-5 h-5 text-primary" />
 <h2 className="text-xl font-semibold text-foreground">{t('Behavior Snapshot')}</h2>
 <span className="text-xs text-foreground ml-2">({t('Click any card for evidence')})</span>
 </div>
 <p className="text-sm text-foreground mb-6">
 {t('Scannable in 10-15 seconds -- Answers:"Is anything off?"', { nsSeparator: false })}
 </p>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {mockBehaviors.map((behavior) => (
 <button
 key={behavior.id}
 className={`p-4 border rounded-xl text-left transition-colors ${getPatternColor(behavior.pattern)}`}
 >
 <div className="flex items-center justify-between mb-2">
 <h3 className="text-sm font-semibold text-foreground">{behavior.name}</h3>
 {getTrendIcon(behavior.trend)}
 </div>
 <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getPatternBadgeColor(behavior.pattern)}`}>
 {t(behavior.patternLabel)}
 </span>
 </button>
 ))}
 </div>
 </div>

 {/* 2. What Changed Recently */}
 <div className="mb-8">
 <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
 <AlertCircle className="w-5 h-5 text-primary" />
 <span>{t('What Changed Recently')}</span>
 </h2>
 <div className="space-y-3">
 {mockRecentChanges.map((change, idx) => (
 <div key={idx} className="bg-card border border-border rounded-xl p-4 flex items-start space-x-3">
 {getChangeIcon(change.type)}
 <div className="flex-1">
 <p className="text-sm text-foreground">{change.summary}</p>
 <span className="text-xs text-foreground">{change.timeframe}</span>
 </div>
 </div>
 ))}
 </div>
 </div>

 {/* Progressive Disclosure Notice */}
 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
 <div className="flex items-start space-x-3">
 <Info className="w-5 h-5 text-blue-600 mt-0.5" />
 <div>
 <p className="text-sm font-medium text-blue-900 mb-1">{t('Progressive Disclosure Design')}</p>
 <p className="text-sm text-blue-700">
 {t('Default view answers:"Is anything off?" -- Click any behavior card to see:"Why?" and"What should we do?"', { nsSeparator: false })}
 </p>
 </div>
 </div>
 </div>

 {/* Action Buttons */}
 <div className="flex items-center justify-end space-x-4">
 <button
 className="px-4 py-2 bg-primary text-white rounded-lg"
 >
 {t('Rate Behavior')}
 </button>
 <button
 className="px-4 py-2 bg-primary text-white rounded-lg"
 >
 {t('Submit Assessment')}
 </button>
 <button
 className="px-4 py-2 border border-border text-foreground rounded-lg"
 >
 {t('Save Draft')}
 </button>
 </div>
 </div>
 );
}
